import {
  PurchaseRequestFilePayload,
  PurchaseRequestItemV2,
  PurchaseRequestV2,
} from "../../../types";

/**
 * Pure, render-free helpers pulled out of the pre-Carbon `PurchaseRequestV2Detail.tsx` (Task 5
 * migration) so the request/item budget-rollup, file-normalisation and change-detection rules are
 * testable without rendering the screen — same convention `purchaseRequestListHelpers.ts` and
 * `NewRequest/itemStatus.ts` already use for this flow. Behaviour is copied verbatim; nothing here
 * changes what the pre-Carbon screen computed.
 */

/** Only a real server UUID is round-tripped as a file `id`; a client-generated temp id is dropped
 * from the update payload so the backend treats it as a new attachment (unchanged from before). */
export const isUUID = (value?: string): boolean =>
  !!value &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const normalizeFiles = (
  files: PurchaseRequestFilePayload[]
): PurchaseRequestFilePayload[] =>
  files.map((file) => {
    const payload: PurchaseRequestFilePayload = {
      fileURL: file.fileURL,
      name: file.name,
      type: file.type,
    };
    if (file.id && isUUID(file.id)) {
      payload.id = file.id;
    }
    return payload;
  });

export interface BudgetSummary {
  itemsWithBudget: PurchaseRequestItemV2[];
  hasBudget: boolean;
  /** Every visible item shares one identical budget value/currency/type — the "global budget"
   * editor shows in that case, the per-item budget column otherwise. */
  allBudgetsSame: boolean;
  showBudgetPerItem: boolean;
}

export const computeBudgetSummary = (
  activeItems: PurchaseRequestItemV2[]
): BudgetSummary => {
  const itemsWithBudget = activeItems.filter((item) => item.budgetValue != null);
  const hasBudget = itemsWithBudget.length > 0;
  const allBudgetsSame =
    hasBudget &&
    activeItems.length === itemsWithBudget.length &&
    itemsWithBudget.every(
      (item) =>
        item.budgetValue === itemsWithBudget[0].budgetValue &&
        item.currency === itemsWithBudget[0].currency &&
        item.budgetType === itemsWithBudget[0].budgetType
    );
  return {
    itemsWithBudget,
    hasBudget,
    allBudgetsSame,
    showBudgetPerItem: hasBudget && !allBudgetsSame,
  };
};

export interface GeneralFormState {
  name: string;
  comments: string;
  originChannel: string;
  originMetadata: string;
  draft: boolean;
  files: PurchaseRequestFilePayload[];
}

/** Whether the editable general-info form differs from the persisted request — drives the Save
 * button's enabled state, verbatim from the pre-Carbon screen. */
export const hasGeneralChanges = (
  form: GeneralFormState,
  request: PurchaseRequestV2
): boolean =>
  form.name !== (request.name || "") ||
  form.comments !== (request.comments || "") ||
  form.originChannel !== (request.originChannel || "") ||
  form.originMetadata !== (request.originMetadata || "") ||
  form.draft !== request.draft ||
  form.files.length !== (request.files?.length || 0) ||
  form.files.some((file, index) => {
    const originalFile = request.files?.[index];
    return (
      file.fileURL !== originalFile?.fileURL ||
      file.name !== originalFile?.name ||
      file.type !== originalFile?.type
    );
  });
